'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { useAuth } from '@/contexts/AuthContext';
import { Search, Bookmark, Menu, X, Sparkles, ChevronDown, LogOut, Settings, PenLine, User, Loader2, Bell } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import NotificationDropdown from '@/components/NotificationDropdown';

const navItems = [
  { label: 'Runway', href: '/runway-feed', key: 'nav-runway' },
];

export default function Topbar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, profile, signOut, loading } = useAuth();
  const { unreadCount } = useNotifications();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      router?.push('/sign-up-login-screen');
      router?.refresh();
    } catch {
      // ignore
    } finally {
      setSigningOut(false);
      setProfileOpen(false);
    }
  };

  const getInitial = () => {
    if (profile?.display_name) return profile?.display_name?.charAt(0)?.toUpperCase();
    if (user?.email) return user?.email?.charAt(0)?.toUpperCase();
    return '?';
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-cream-300">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex items-center gap-2 shrink-0">
          <Link href="/runway-feed" className="flex items-center gap-3 group">
            {/* Desktop logo */}
            <span
              className="hidden sm:flex items-center gap-2.5 select-none"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              {/* Large decorative S monogram */}
              <span
                className="text-2xl font-bold text-charcoal-900 group-hover:text-charcoal-600 transition-colors duration-300"
                style={{ letterSpacing: '-0.02em', lineHeight: 1 }}
              >
                S
              </span>
              {/* Thin subtle vertical separator */}
              <span
                className="w-px h-5 transition-colors duration-300"
                style={{ background: 'rgba(100,100,100,0.25)' }}
                aria-hidden="true"
              />
              {/* Wordmark — full SILHOUETTE */}
              <span
                className="text-xs font-medium text-charcoal-900 group-hover:text-charcoal-600 transition-colors duration-300"
                style={{ letterSpacing: '0.38em' }}
              >
                SILHOUETTE
              </span>
            </span>
            {/* Mobile: just the S */}
            <span
              className="sm:hidden"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              <span className="text-2xl font-bold text-charcoal-900" style={{ letterSpacing: '-0.02em' }}>S</span>
            </span>
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navItems?.map((item) => (
            <Link key={item?.key} href={item?.href}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${pathname === item?.href ? 'bg-charcoal-900 text-white' : 'text-charcoal-600 hover:bg-cream-200 hover:text-charcoal-900'}`}>
              {item?.label}
            </Link>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          <button onClick={() => setSearchOpen(!searchOpen)}
            className="p-2 rounded-lg text-charcoal-500 hover:bg-cream-200 hover:text-charcoal-900 transition-all duration-150">
            <Search size={18} />
          </button>

          {user && (
            <>
              <Link href="/profile-management" className="p-2 rounded-lg text-charcoal-500 hover:bg-cream-200 hover:text-charcoal-900 transition-all duration-150 hidden sm:flex">
                <Bookmark size={18} />
              </Link>

              {/* Notification Bell */}
              <div className="relative hidden sm:block">
                <button
                  onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }}
                  className="relative p-2 rounded-lg text-charcoal-500 hover:bg-cream-200 hover:text-charcoal-900 transition-all duration-150"
                  aria-label="Notifications"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-charcoal-900 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <NotificationDropdown open={notifOpen} onClose={() => setNotifOpen(false)} />
              </div>

              {(profile?.role === 'icon' || profile?.role === 'admin') && (
                <Link href="/create-post"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-charcoal-900 text-white rounded-lg text-sm font-medium hover:bg-charcoal-800 transition-all duration-150">
                  <PenLine size={14} />
                  <span>Create</span>
                </Link>
              )}

              {profile?.role === 'icon' && (
                <div className="hidden sm:flex items-center gap-1 px-2 py-1 bg-gold-50 border border-gold-200 rounded-full">
                  <Sparkles size={11} className="text-gold-500" />
                  <span className="text-xs font-medium text-gold-600">Icon</span>
                </div>
              )}
            </>
          )}

          {loading ? (
            <div className="p-2"><Loader2 size={16} className="animate-spin text-charcoal-400" /></div>
          ) : user ? (
            <div className="relative">
              <button onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-cream-200 transition-all duration-150">
                {profile?.avatar_url ? (
                  <img src={profile?.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gold-300 to-gold-500 flex items-center justify-center text-xs font-semibold text-white">
                    {getInitial()}
                  </div>
                )}
                <span className="text-sm font-medium text-charcoal-900 hidden sm:block">
                  {profile?.display_name?.split(' ')?.[0] || 'Account'}
                </span>
                <ChevronDown size={14} className={`text-charcoal-500 transition-transform duration-150 ${profileOpen ? 'rotate-180' : ''}`} />
              </button>

              {profileOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl border border-cream-300 shadow-lg py-1 fade-in">
                  <div className="px-4 py-2.5 border-b border-cream-200">
                    <p className="text-sm font-semibold text-charcoal-900">{profile?.display_name || 'User'}</p>
                    <p className="text-xs text-charcoal-400">@{profile?.username}</p>
                  </div>
                  <Link href={`/profile/${profile?.username || user?.id}`}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-charcoal-700 hover:bg-cream-100 transition-colors"
                    onClick={() => setProfileOpen(false)}>
                    <User size={15} />
                    View Profile
                  </Link>
                  <Link href="/profile-management"
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-charcoal-700 hover:bg-cream-100 transition-colors"
                    onClick={() => setProfileOpen(false)}>
                    <Settings size={15} />
                    Settings
                  </Link>
                  <hr className="my-1 border-cream-300" />
                  <button onClick={handleSignOut} disabled={signingOut}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                    {signingOut ? <Loader2 size={15} className="animate-spin" /> : <LogOut size={15} />}
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link href="/sign-up-login-screen"
              className="flex items-center gap-1.5 px-4 py-2 bg-charcoal-900 text-white rounded-lg text-sm font-semibold hover:bg-charcoal-800 transition-all duration-150">
              Sign In
            </Link>
          )}

          <button onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-lg text-charcoal-500 hover:bg-cream-200 transition-all duration-150">
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {searchOpen && (
        <div className="border-t border-cream-300 px-6 lg:px-8 py-3 bg-white fade-in">
          <div className="max-w-2xl mx-auto relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
            <input autoFocus type="text" placeholder="Search icons, editorials, brands…"
              className="w-full pl-9 pr-4 py-2.5 bg-cream-100 border border-cream-300 rounded-lg text-sm text-charcoal-900 placeholder:text-charcoal-400 focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400 transition-all" />
          </div>
        </div>
      )}
      {mobileOpen && (
        <div className="md:hidden border-t border-cream-300 bg-white fade-in">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {navItems?.map((item) => (
              <Link key={item?.key} href={item?.href} onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-150 ${pathname === item?.href ? 'bg-charcoal-900 text-white' : 'text-charcoal-700 hover:bg-cream-200'}`}>
                {item?.label}
              </Link>
            ))}
            {user && (profile?.role === 'icon' || profile?.role === 'admin') && (
              <Link href="/create-post" onClick={() => setMobileOpen(false)}
                className="px-4 py-3 rounded-lg text-sm font-medium text-charcoal-700 hover:bg-cream-200 transition-all flex items-center gap-2">
                <PenLine size={14} />
                Create Post
              </Link>
            )}
            <hr className="border-cream-300 my-1" />
            {user ? (
              <button onClick={handleSignOut}
                className="px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-all text-left">
                Sign Out
              </button>
            ) : (
              <Link href="/sign-up-login-screen" onClick={() => setMobileOpen(false)}
                className="px-4 py-3 rounded-lg text-sm font-medium text-charcoal-700 hover:bg-cream-200 transition-all">
                Sign In
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}